import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, money } from "@/lib/api";
import { partsDepuisResas } from "@/lib/partage";

const num = (v) => Number(String(v).replace(",", ".").replace(/\s/g, ""));
const vide = { immeuble: "", montant: "", tps: "", tvq: "" };
const depuisResas = (op) => {
  const lignes = partsDepuisResas(op.reservations, op.debit || op.credit || 0, "immeuble").map((p) => ({ ...vide, immeuble: p.nom, montant: String(p.montant).replace(".", ",") }));
  return lignes.length === 1 ? [...lignes, { ...vide }] : lignes;
};
const depuisOp = (op) => (op.parts?.length
  ? op.parts.map((p) => ({ immeuble: p.immeuble, montant: String(p.montant), tps: p.tps_auto ? "" : String(Math.abs(p.tps)), tvq: p.tvq_auto ? "" : String(Math.abs(p.tvq)) }))
  : depuisResas(op).length ? depuisResas(op) : [{ ...vide }, { ...vide }]);

const Ligne = ({ p, i, total, immeubles, part, onChange, onRemove }) => {
  const pct = total && num(p.montant) ? `${((num(p.montant) / total) * 100).toLocaleString("fr-CA", { maximumFractionDigits: 1 })} %` : "";
  const taxe = (k) => (part ? Math.abs(part[k] || part[`${k}_nc`] || 0).toFixed(2).replace(".", ",") : "auto");
  return (
    <div className="grid grid-cols-2 sm:grid-cols-[1fr_120px_56px_110px_110px_auto] gap-2 items-center" data-testid={`repartition-part-${i}`}>
      <div className="col-span-2 sm:col-span-1">
        <Select value={p.immeuble || undefined} onValueChange={(immeuble) => onChange({ ...p, immeuble })}>
          <SelectTrigger className="h-9 bg-white" data-testid={`repartition-immeuble-${i}`}><SelectValue placeholder="Immeuble" /></SelectTrigger>
          <SelectContent>{immeubles.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Input value={p.montant} onChange={(e) => onChange({ ...p, montant: e.target.value })} inputMode="decimal" placeholder="Montant $" className="h-9 text-right font-mono text-xs" data-testid={`repartition-montant-${i}`} />
      <span className="text-xs text-slate-500 tabular" data-testid={`repartition-pct-${i}`}>{pct}</span>
      {["tps", "tvq"].map((k) => (
        <Input key={k} value={p[k]} onChange={(e) => onChange({ ...p, [k]: e.target.value })} inputMode="decimal" placeholder={taxe(k)} aria-label={k.toUpperCase()}
          className="h-9 text-right font-mono text-xs" title={part?.taxes_note || "Vide = calcul automatique selon la taxabilité de l'immeuble"} data-testid={`repartition-${k}-${i}`} />
      ))}
      <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={onRemove} data-testid={`repartition-retirer-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
    </div>
  );
};

export const RepartitionDialog = ({ op, open, onClose, onSaved }) => {
  const [lignes, setLignes] = useState(depuisOp(op));
  const [immeubles, setImmeubles] = useState([]);
  const [courant, setCourant] = useState(op);
  const total = op.debit || op.credit || 0;
  useEffect(() => {
    if (open) {
      setLignes(depuisOp(op)); setCourant(op);
      api.get("/immeubles").then((r) => setImmeubles(r.data.map((i) => i.nom))).catch((e) => toast.error(errMsg(e)));
    }
  }, [open, op]);
  const reste = Math.round((total - lignes.reduce((s, p) => s + (num(p.montant) || 0), 0)) * 100) / 100;
  const envoyer = async (parts) => {
    try {
      const { data } = await api.put(`/transactions/${op.id}/repartition`, { parts });
      toast.success(parts.length ? "Répartition enregistrée — vérifiez les taxes par part" : "Répartition annulée");
      setCourant(data); setLignes(depuisOp(data)); onSaved();
      if (!parts.length) onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const enregistrer = () => envoyer(lignes.filter((p) => p.immeuble || p.montant).map((p) => ({
    immeuble: p.immeuble, montant: num(p.montant),
    ...(p.tps.trim() ? { tps: Math.abs(num(p.tps)) } : {}), ...(p.tvq.trim() ? { tvq: Math.abs(num(p.tvq)) } : {}),
  })));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="repartition-dialog">
        <DialogHeader>
          <DialogTitle>Répartir entre immeubles · {money(total)}</DialogTitle>
          <DialogDescription>{op.date} — {op.description}. Saisissez le montant de chaque immeuble. TPS / TVQ : vide = calcul automatique selon la taxabilité de l'immeuble ; saisissez un montant pour corriger.</DialogDescription>
        </DialogHeader>
        <div className="hidden sm:grid grid-cols-[1fr_120px_56px_110px_110px_32px] gap-2 text-[10px] font-mono uppercase tracking-wider text-slate-500"><span>Immeuble</span><span className="text-right">Montant</span><span>%</span><span className="text-right">TPS</span><span className="text-right">TVQ</span><span /></div>
        <div className="space-y-2">
          {lignes.map((p, i) => (
            <Ligne key={i} p={p} i={i} total={total} immeubles={immeubles} part={courant.parts?.find((x) => x.immeuble === p.immeuble)}
              onChange={(v) => setLignes(lignes.map((x, j) => (j === i ? v : x)))} onRemove={() => setLignes(lignes.filter((_, j) => j !== i))} />
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={() => setLignes([...lignes, { ...vide }])} data-testid="repartition-ajouter"><Plus className="h-3.5 w-3.5 mr-1" />Immeuble</Button>
          <span className={`text-sm font-medium tabular pr-1 ${Math.abs(reste) < 0.01 ? "text-emerald-700" : "text-amber-700"}`} data-testid="repartition-reste">
            {Math.abs(reste) < 0.01 ? "Montant entièrement réparti" : `Reste à répartir : ${money(reste)}`}
          </span>
        </div>
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          {op.parts?.length > 0 && <Button variant="ghost" className="text-red-600" onClick={() => envoyer([])} data-testid="repartition-annuler">Annuler la répartition</Button>}
          <Button variant="outline" onClick={onClose} data-testid="repartition-fermer">Fermer</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" disabled={Math.abs(reste) >= 0.01} onClick={enregistrer} data-testid="repartition-enregistrer">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
